/**
 * PosInvoiceConfigSection · Invoice Numbering configuration surface.
 *
 * Where it lives: injected into `/pos/settings` under the appearance
 * card, so tenant admins configure numbering in the same screen they
 * use for staff. Uses the platform JWT (not a POS session), consistent
 * with the rest of PosSettings.
 *
 * Route (backend): `/api/pos/admin/outlets/{outlet_id}/invoice-config`
 *   • GET     — current cfg (saved or default) + `next_invoice_no`
 *   • POST    — /preview (no persistence) — used to render the live
 *                sample number as the cashier edits fields.
 *   • PUT     — save. Rule 46(b) is enforced server-side; the server's
 *                plain-language error is displayed verbatim.
 *
 * Deadlock context (Sprint 3B correction, 6 Feb 2026): before this
 * screen existed, `print-bill` refused with a 409 telling the admin
 * to configure Invoice Numbering — with no screen to configure. The
 * fix has TWO parts:
 *   1. THIS screen · so overrides are possible
 *   2. The default fallback in `_load_invoice_config` · so an outlet
 *      with nothing saved still gets `{outlet_code}/{FY}/{seq}` and
 *      prints on day one. Configuration is an OVERRIDE, never a gate.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { Receipt, Info } from "lucide-react";


const FY_FORMATS      = ["YY-YY", "YYYY-YY", "YYYY-YYYY"];
const ROUNDING_MODES  = ["total_only", "per_component"];
// Same regex the server enforces on save; we run it client-side so
// the field turns red as the admin types instead of only after Save.
const OUTLET_CODE_RE  = /^[A-Za-z0-9]{2,4}$/;
const SERIES_CODE_RE  = /^[A-Za-z0-9]{1,3}$/;

const DEFAULTS = {
  outlet_code:     "",
  prefix_template: "{outlet_code}/{FY}/{seq}",
  fy_format:       "YY-YY",
  seq_length:      4,
  rounding_mode:   "total_only",
  series_default:  "A",
};


export default function PosInvoiceConfigSection({ outletId, outletName }) {
  const { API, getHeaders } = useAuth();
  // `useAuth().API` already includes the `/api` prefix
  // (`AuthContext.js:6`: `const API = ${BACKEND_URL}/api`). Appending
  // another `/api` here would produce `.../api/api/...` and 404.
  const base = `${API}/pos/admin/outlets/${outletId}/invoice-config`;

  const [form,         setForm]         = useState(DEFAULTS);
  const [source,       setSource]       = useState(null);   // "saved" | "default"
  const [preview,      setPreview]      = useState("");
  const [serverError,  setServerError]  = useState("");
  const [saving,       setSaving]       = useState(false);
  const [loading,      setLoading]      = useState(true);

  const clientError = useMemo(() => {
    if (!OUTLET_CODE_RE.test((form.outlet_code || "").toUpperCase())) {
      return "Outlet code must be 2 to 4 letters or digits (for example, RB or DAMAN).";
    }
    if (!SERIES_CODE_RE.test((form.series_default || "").toUpperCase())) {
      return "Default series code must be 1 to 3 letters or digits (for example, A or L01).";
    }
    if (!(form.prefix_template || "").includes("{outlet_code}")
        || !(form.prefix_template || "").includes("{seq}")) {
      return "Invoice format must include both {outlet_code} and {seq}. {FY} is strongly recommended for statutory uniqueness within a financial year.";
    }
    if (form.seq_length < 3 || form.seq_length > 6) {
      return "Sequence length must be between 3 and 6 digits.";
    }
    return "";
  }, [form]);

  // ── initial load ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(base, { headers: getHeaders() })
      .then((r) => {
        if (cancelled) return;
        const cfg = r.data.invoice_config || DEFAULTS;
        setForm({
          outlet_code:     cfg.outlet_code || "",
          prefix_template: cfg.prefix_template || DEFAULTS.prefix_template,
          fy_format:       cfg.fy_format || DEFAULTS.fy_format,
          seq_length:      cfg.seq_length || DEFAULTS.seq_length,
          rounding_mode:   cfg.rounding_mode || DEFAULTS.rounding_mode,
          series_default:  cfg.series_default || DEFAULTS.series_default,
        });
        setSource(cfg._source || null);
        setPreview(r.data.next_invoice_no || "");
        setServerError("");
      })
      .catch((e) => {
        toast.error(`Could not load invoice configuration: ${e?.response?.data?.detail || e.message}`);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [base, getHeaders]);

  // ── live preview as fields change ───────────────────────────────
  const refreshPreview = useCallback(async (draft) => {
    if (clientError) { setPreview(""); return; }
    try {
      const r = await axios.post(`${base}/preview`, {
        ...draft,
        outlet_code:    draft.outlet_code.toUpperCase(),
        series_default: draft.series_default.toUpperCase(),
      }, { headers: getHeaders() });
      setPreview(r.data.next_invoice_no || "");
      setServerError("");
    } catch (e) {
      setPreview("");
      // The server error message is plain language by design — display
      // it verbatim so the admin reads the real reason, not a regex.
      setServerError(e?.response?.data?.detail || "Preview failed.");
    }
  }, [base, clientError, getHeaders]);

  useEffect(() => {
    // Debounce the /preview call so typing doesn't hammer the server.
    const h = setTimeout(() => refreshPreview(form), 300);
    return () => clearTimeout(h);
  }, [form, refreshPreview]);

  const save = async () => {
    setSaving(true);
    setServerError("");
    try {
      const r = await axios.put(base, {
        ...form,
        outlet_code:    form.outlet_code.toUpperCase(),
        series_default: form.series_default.toUpperCase(),
      }, { headers: getHeaders() });
      const cfg = r.data.invoice_config;
      setSource(cfg._source || "saved");
      setPreview(r.data.next_invoice_no || "");
      toast.success("Invoice numbering saved.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed.";
      setServerError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const isDefault = source === "default";
  const canSave   = !clientError && !saving && !!preview;

  if (loading) return null;

  return (
    <Card className="border-[#E2E8DF]" data-testid="pos-invoice-config-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[color:var(--color-primary)]" />
              <h2 className="text-[18px] font-semibold text-[#1A2421]">Invoice Numbering</h2>
              {isDefault && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-widest border-amber-300 text-amber-800 bg-amber-50"
                       data-testid="pos-invoice-config-default-badge">
                  Using default
                </Badge>
              )}
              {!isDefault && source === "saved" && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-widest border-emerald-300 text-emerald-800 bg-emerald-50"
                       data-testid="pos-invoice-config-saved-badge">
                  Saved configuration
                </Badge>
              )}
            </div>
            <p className="text-[13px] text-[#5C6B64] mt-1">
              {outletName ? `${outletName} — ` : ""}every invoice number stays under 16 characters and uses only letters, numbers, hyphen and slash (Rule 46(b)).
            </p>
          </div>
        </div>

        {isDefault && (
          <div className="mb-4 p-3 rounded-md border border-amber-200 bg-amber-50 flex items-start gap-2 text-[12px] text-amber-900"
               data-testid="pos-invoice-config-default-hint">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              The outlet is currently printing with an auto-generated default
              (<span className="font-mono font-semibold">{preview || "—"}</span>).
              Adjust the fields below and press Save to lock in your own format.
            </div>
          </div>
        )}

        {/* Live preview — the number the very next bill will print. */}
        <div className="mb-6 p-4 rounded-lg border border-[#E2E8DF] bg-[#F9F9F8]" data-testid="pos-invoice-config-preview-block">
          <div className="text-[10px] uppercase tracking-widest text-[#5C6B64] font-semibold">Next invoice number</div>
          <div className="mt-1 flex items-baseline gap-3">
            <span className="text-[36px] leading-none font-semibold text-[#1A2421] tabular-nums font-mono"
                  data-testid="pos-invoice-config-preview">
              {preview || "—"}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-[12px]">Outlet code</Label>
            <Input value={form.outlet_code}
                   onChange={(e) => set("outlet_code")(e.target.value.toUpperCase())}
                   maxLength={4}
                   placeholder="RB"
                   className="mt-1 font-mono uppercase"
                   data-testid="pos-invoice-config-outlet-code" />
            <p className="text-[11px] text-[#5C6B64] mt-1">2–4 letters or digits. Appears at the start of every number.</p>
          </div>
          <div>
            <Label className="text-[12px]">Default series</Label>
            <Input value={form.series_default}
                   onChange={(e) => set("series_default")(e.target.value.toUpperCase())}
                   maxLength={3}
                   placeholder="A"
                   className="mt-1 font-mono uppercase"
                   data-testid="pos-invoice-config-series" />
            <p className="text-[11px] text-[#5C6B64] mt-1">Single series today. A separate liquor series later is a configuration change, not a migration.</p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[12px]">Invoice format</Label>
            <Input value={form.prefix_template}
                   onChange={(e) => set("prefix_template")(e.target.value)}
                   className="mt-1 font-mono"
                   data-testid="pos-invoice-config-prefix" />
            <p className="text-[11px] text-[#5C6B64] mt-1">
              Uses <span className="font-mono">{"{outlet_code}"}</span>, <span className="font-mono">{"{FY}"}</span>, <span className="font-mono">{"{seq}"}</span> placeholders. Everything else must be letters, digits, hyphen or slash.
            </p>
          </div>
          <div>
            <Label className="text-[12px]">Financial year format</Label>
            <Select value={form.fy_format} onValueChange={set("fy_format")}>
              <SelectTrigger className="mt-1" data-testid="pos-invoice-config-fy-format">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FY_FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[12px]">Sequence length (digits)</Label>
            <Select value={String(form.seq_length)} onValueChange={(v) => set("seq_length")(Number(v))}>
              <SelectTrigger className="mt-1" data-testid="pos-invoice-config-seq-length">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>{n} — up to {(10 ** n - 1).toLocaleString()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[12px]">Rounding mode</Label>
            <Select value={form.rounding_mode} onValueChange={set("rounding_mode")}>
              <SelectTrigger className="mt-1" data-testid="pos-invoice-config-rounding">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="total_only">Total only — round-off adjusts the final payable to the nearest rupee</SelectItem>
                <SelectItem value="per_component">Per-component — each tax component rounds independently</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {(clientError || serverError) && (
          <div className="mt-4 p-3 rounded-md border border-rose-200 bg-rose-50 text-[12px] text-rose-900"
               data-testid="pos-invoice-config-error">
            {clientError || serverError}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-3">
          <Button variant="outline" disabled={saving}
                  onClick={() => refreshPreview(form)}
                  data-testid="pos-invoice-config-refresh-preview"
                  className="border-[#E2E8DF] text-[#1A2421] hover:bg-[#F9F9F8]">
            Refresh preview
          </Button>
          <Button onClick={save} disabled={!canSave}
                  data-testid="pos-invoice-config-save"
                  className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white">
            {saving ? "Saving…" : "Save invoice numbering"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
